import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, PhoneCall, History, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVehicles, vehicleName } from "@/lib/useVehicles";
import QuickConvertDialog from "./QuickConvertDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", phone: "", email: "", date_of_birth: "", vehicle: "",
  pickup_date: "", pickup_time: "10:00", return_date: "", return_time: "10:00",
  rental_purpose: "Personal Transportation", dl_state: "", address: "", admin_notes: "",
};

const DOC_UPLOAD_FIELDS = ["license_front", "license_back", "insurance_card", "selfie"];

const inputCls = "mt-1 w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50 [color-scheme:dark]";

export default function ManualReservation({ token, onCreated }) {
  const { vehicles } = useVehicles();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [sendDocsLink, setSendDocsLink] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState(null); // { matches, dnr_match }
  const [convertReq, setConvertReq] = useState(null);
  const lookupTimer = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    clearTimeout(lookupTimer.current);
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 7) { setLookup(null); return; }
    lookupTimer.current = setTimeout(() => {
      axios.get(`${API}/reservations/lookup-caller?phone=${encodeURIComponent(form.phone)}`,
        { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => setLookup(data)).catch(() => setLookup(null));
    }, 400);
    return () => clearTimeout(lookupTimer.current);
    // eslint-disable-next-line
  }, [form.phone]);

  const applyMatch = (m) => {
    setForm((f) => ({ ...f, name: m.name || f.name, email: m.email || f.email,
      address: m.address || f.address, dl_state: m.dl_state || f.dl_state,
      date_of_birth: m.date_of_birth || f.date_of_birth }));
    toast.success(`Pulled in ${m.name}'s info from their ${m.type === "booking" ? "past booking" : "past request"}.`);
    setLookup(null);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.phone.trim() && !form.email.trim()) return toast.error("Phone or email is required.");
    setBusy(true);
    const H = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const { data } = await axios.post(`${API}/reservations/manual`, form, H);
      toast.success(data.message || `Reservation request added for ${form.name}.`);
      if (sendDocsLink && data.request_id) {
        try {
          await axios.post(`${API}/reservations/${data.request_id}/request-docs`,
            { docs: DOC_UPLOAD_FIELDS, note: "" }, H);
          toast.success("Secure document upload link texted & emailed to the customer.");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Reservation added, but the document upload link could not be sent — send it manually from the request's Documents section.");
        }
      }
      const created = { id: data.request_id, name: form.name, vehicle: form.vehicle,
        pickup_date: form.pickup_date, return_date: form.return_date };
      setForm(empty); setLookup(null); setOpen(false); onCreated?.();
      if (created.pickup_date && created.return_date) setConvertReq(created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the reservation request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid="new-manual-reservation-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm bg-[#F2B705] text-black font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
        <Plus size={16} /> Add Reservation
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="manual-reservation-dialog" className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><PhoneCall size={18} className="text-[#F2B705]" /> New Reservation Request (Phone / Walk-in)</DialogTitle>
            <DialogDescription className="text-white/45">Takes a request straight into the queue below — the same screening, documents and approval pipeline as a website submission. The Do-Not-Rent list is still enforced.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Name</label><input data-testid="mr-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Phone</label>
                <input data-testid="mr-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="type to check for repeat callers" />
              </div>
            </div>

            {lookup?.dnr_match && (
              <div data-testid="mr-dnr-warning" className="flex items-center gap-2 bg-red-950/40 border border-red-500/40 rounded-md px-3 py-2 text-[11px] text-red-300">
                <ShieldAlert size={13} /> This number matches the Do-Not-Rent list ({lookup.dnr_match}). The request will be blocked on submit — check the Do Not Rent tab before proceeding.
              </div>
            )}
            {lookup?.matches?.length > 0 && (
              <div data-testid="mr-caller-matches" className="bg-sky-950/20 border border-sky-500/25 rounded-md p-2.5 space-y-1.5">
                <div className="flex items-center gap-1.5 text-sky-300 text-[11px] font-bold"><History size={12} /> Repeat caller — past records for this number:</div>
                {lookup.matches.slice(0, 4).map((m) => (
                  <button key={`${m.type}-${m.id}`} type="button" data-testid={`mr-match-${m.id}`} onClick={() => applyMatch(m)}
                    className="w-full text-left bg-black/40 hover:bg-black/60 border border-white/10 rounded px-2.5 py-1.5 text-[11px] text-white/70 transition-colors">
                    <span className="font-semibold text-white">{m.name}</span> · {m.email || "no email"} · {m.vehicle || "any vehicle"} · {m.pickup_date?.slice(0, 10)}→{m.return_date?.slice(0, 10)} · <span className="text-white/45">{m.status}</span>
                    {m.balance > 0 && <span className="text-red-300 font-bold"> · balance ${m.balance}</span>}
                    <span className="block text-[#F2B705] text-[10px] mt-0.5">Tap to fill name/email/address/license from this record →</span>
                  </button>
                ))}
              </div>
            )}

            <div><label className="text-xs uppercase tracking-widest text-white/45">Email</label><input data-testid="mr-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="optional if phone given" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Vehicle <span className="text-white/30 normal-case">(optional)</span></label>
                <select data-testid="mr-vehicle" className={inputCls} value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)}>
                  <option value="">Any / not decided yet</option>
                  {vehicles.map((c) => <option key={c.id} value={vehicleName(c)}>{c.year} {vehicleName(c)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Purpose</label>
                <select data-testid="mr-purpose" className={inputCls} value={form.rental_purpose} onChange={(e) => set("rental_purpose", e.target.value)}>
                  <option>Personal Transportation</option>
                  <option>Uber or Lyft</option>
                  <option>Delivery or Gig Work</option>
                  <option>Business Use</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Pickup Date</label><input data-testid="mr-pickup" type="date" className={inputCls} value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">Return Date</label><input data-testid="mr-return" type="date" className={inputCls} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Pickup Time</label><input data-testid="mr-pickup-time" type="time" className={inputCls} value={form.pickup_time} onChange={(e) => set("pickup_time", e.target.value)} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">Return Time</label><input data-testid="mr-return-time" type="time" className={inputCls} value={form.return_time} onChange={(e) => set("return_time", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase tracking-widest text-white/45">Date of Birth <span className="text-white/30 normal-case">(optional)</span></label><input data-testid="mr-dob" type="date" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
              <div><label className="text-xs uppercase tracking-widest text-white/45">License State <span className="text-white/30 normal-case">(optional)</span></label><input data-testid="mr-dl-state" className={inputCls} maxLength={2} value={form.dl_state} onChange={(e) => set("dl_state", e.target.value.toUpperCase())} /></div>
            </div>
            <div><label className="text-xs uppercase tracking-widest text-white/45">Address <span className="text-white/30 normal-case">(optional)</span></label><input data-testid="mr-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><label className="text-xs uppercase tracking-widest text-white/45">Internal Notes <span className="text-white/30 normal-case">(optional)</span></label><textarea data-testid="mr-notes" rows={2} className={inputCls} value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} placeholder="Anything from the call worth flagging for review" /></div>

            <label className="flex items-start gap-2 text-xs text-white/70 bg-[#F2B705]/[0.06] border border-[#F2B705]/25 rounded-lg px-3 py-2.5 cursor-pointer">
              <input data-testid="mr-send-docs-link" type="checkbox" checked={sendDocsLink} onChange={(e) => setSendDocsLink(e.target.checked)} className="mt-0.5 accent-[#F2B705]" />
              <span>Also text &amp; email them a secure link right now to upload their <b className="text-[#F2B705]">driver's license (front &amp; back), insurance card, and a selfie</b>.</span>
            </label>

            <button data-testid="mr-submit" onClick={submit} disabled={busy}
              className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Add to Reservation Queue
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <QuickConvertDialog request={convertReq} token={token} onClose={() => setConvertReq(null)} onDone={onCreated} />
    </>
  );
}

